import { ReactNode, Suspense, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { ToastContainer } from "react-toastify";
import { createTheme as createMuiTheme, ThemeProvider } from "@mui/material/styles";
import ReactGA from "react-ga4";
import ActionSpinner from "./config/axios/ActionSpinner/ActionSpinner";
import CircularProgressSpinner from "./config/axios/CircularProgressbar/CircularProgressSpinner";
import { darkThemeConfig, defaultThemeConfig, generateTheme } from "./utils/muiUtils";
import { ColorModeProvider, useColorMode } from "./theme/ColorModeContext";
import AppRoute from "./route/AppRoute";
import { initObject } from "./config/analytics/analytics";
import useAsyncError from "./Hooks/useAsyncError";
import { DEFAULT_TENANT_ID, NODE_ENV, RECAPTCHA_SITE_KEY } from "./config/app.config";
import { BO_CORE_DEFAULT_VALUE, isCancelledErrorProcessor } from "./utils/AppUtils";
import SuspenseSpinner from "./components/Common/SuspenseSpinner/SuspenseSpinner";
import "./utils/modules";
// import "react-toastify/dist/ReactToastify.css";
import useAbortApi from "./Hooks/useAbortApi";
import { uiActions } from "./reducers/uiReducer";
import { setRecaptchaStatus } from "./reducers/RecaptchaStatusReducer";
import { loadRecaptcha } from "./utils/recaptcha/loadRecaptcha";
import { setGoogleMapsStatus } from "./reducers/GoogleMapsStatusReducer";
import { loadGoogleMaps } from "./utils/googleMaps/loadGoogleMaps";
import { initialize } from "./config/axios/Axios";
import GenericGridConfigurationManager from "./contexts/GenericGridConfigurationContext/GenericGridConfigurationContext";
import { getTenantConfig } from "./config/axios/apis/tenant/tenant";
import { isAccessTokenPresent, isRefreshTokenPresent, reconcileOrphanedAssumeSession } from "./utils/AuthUtils/AuthUtils";
import { ASSUMED_TOKEN_STORE_KEY } from "./utils/AuthUtils/TokenStore";
import ShortUrlRouter from "./views/shortUrls/ShortUrlRouter";
import type { IncomingRegistrationConfig } from "./views/publicPages/RegisterV3/utils/registrationConfig";
import { readAndScrubTenantContextFromUrl, seedTenantContextFromSubdomainConfig } from "./utils/navigation/tenantContext";

initialize();

// Toasts follow the color mode (react-toastify's own theming; Graphite surface via the
// --toastify-* overrides in index.css).
const ThemedToastContainer = () => {
    const { mode } = useColorMode();
    return <ToastContainer theme={mode} />;
};

// Picks the MUI theme from the current color mode; both configs come from the same
// buildThemeConfig factory in muiUtils, so component styleOverrides follow the palette.
const ThemedApp = ({ children }: { children: ReactNode }) => {
    const { mode } = useColorMode();
    const theme = useMemo(() => createMuiTheme(mode === "dark" ? darkThemeConfig : defaultThemeConfig), [mode]);
    return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

// NG-8055: drain ?ctx=<tenantId> and ?assume=1 from the URL into sessionStorage before
// any authed API call so per-tab tenant context + assume-identity propagate to new tabs.
readAndScrubTenantContextFromUrl();

// NG-8061 multi-tab assume reconciliation. A tab can be in assume mode while its shared assumed-token
// slot is already gone — a sibling tab released the identity, or this tab was reloaded after that
// happened. Left alone, the re-auth path would seed CS tokens into the assumed slot and strand the
// tab "acting as itself". Revert such tabs to the CS user before any authed call runs (bootstrap
// guard), and keep listening so tabs open when a sibling releases react at once — the `storage` event
// fires only in OTHER tabs, so the acting tab reverts itself directly via releaseAssumedIdentity.
reconcileOrphanedAssumeSession();

window.addEventListener("storage", (event) => {
    // A per-tenant wipe rewrites the map (newValue non-null), so reconcile re-reads the slot rather
    // than trusting the event payload — this also ignores re-seeds of a different tenant's slot.
    if (event.key === ASSUMED_TOKEN_STORE_KEY) {
        reconcileOrphanedAssumeSession();
    }
});

const App = () => {
    const dispatch = useDispatch();
    const [customTheme, setCustomTheme] = useState<any>(null);
    // const isAuthenticated = AuthUtils.isAccessTokenPresent();
    // const user = useSelector((store: any) => store.userReducer.user);
    // const locale = useSelector((store: any) => store.userReducer.locale);
    const throwError = useAsyncError();
    const { signal } = useAbortApi();
    const isAuthenticated = isAccessTokenPresent() || isRefreshTokenPresent();

    let link: any = document.querySelector("link[rel~='icon']");
    if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.getElementsByTagName("head")[0].appendChild(link);
    }

    const createTheme = (themeConfig: any) => {
        const theme = generateTheme(themeConfig);
        setCustomTheme(theme);
    };

    const handleTenantConfigResponse = (response: any) => {
        // On tenant subdomains, route this tab's token-slot lookups to the host's tenant before the
        // route tree renders (rendering is gated on customTheme below) — otherwise a fresh tab
        // defaults to DEFAULT_TENANT_ID, misses the origin's token slot and re-auths a logged-in user.
        seedTenantContextFromSubdomainConfig(response.data.tenantId);
        createTheme(response.data.themeConfiguration.muiTheme);
        if (response.data.publicUrlTitleIcon !== "" && response.data.publicUrlTitleIcon !== BO_CORE_DEFAULT_VALUE) {
            link.href = response.data.publicUrlTitleIcon;
        }

        const googlePlacesApiKey = NODE_ENV === "development" ? "AIzaSyClEvyBCwThKl86W3BZCaouyIBp8rqDoVE" : response.data.googlePlacesApiKey;
        dispatch(uiActions.setGooglePlacesApiKey(googlePlacesApiKey));

        // NG-7511: detect Google Places availability centrally so address fields can fall back to
        // manual entry on outage. Injects the Maps script once; the autocomplete library reuses it.
        loadGoogleMaps(googlePlacesApiKey, (status) => dispatch(setGoogleMapsStatus(status)));

        // NG-7512: detect reCaptcha availability centrally so the public registration forms can
        // fail open (drop the token gate) on outage. Injects api.js once; the widget library reuses it.
        // Only for unauthenticated visitors — the widget lives on public forms alone, and loading a
        // Google script inside the authed (PHI) context is avoided for the same reason GA is gated off.
        if (!isAuthenticated) {
            loadRecaptcha(RECAPTCHA_SITE_KEY, (status) => dispatch(setRecaptchaStatus(status)));
        }

        if (response.data.tenantName && response.data.tenantId !== DEFAULT_TENANT_ID) {
            dispatch(uiActions.setPublicTenantData(response.data));
        }
        // Registration flow config is needed by v3 registration regardless of tenant
        // (including the default), so dispatch it independently of `setPublicTenantData`
        // which is gated to non-default tenants for branding-override reasons.
        // The cast is the wire-shape trust boundary — the BE response is otherwise untyped.
        if (response.data.registrationFlowConfig) {
            dispatch(uiActions.setPublicRegistrationFlowConfig(response.data.registrationFlowConfig as IncomingRegistrationConfig));
        }
    };

    useEffect(() => {
        if (NODE_ENV !== "development" && !isAuthenticated) {
            // Delay the GA initialization because in some cases we need time to disable it.
            // Disabled for authenticated users because of HIPAA requirements.
            // For example when we hit Authorized page GA should be disabled.
            // Use window[`ga-disable-${GOOGLE_MEASUREMENT_ID}`] with value true to disable GA.
            setTimeout(() => {
                ReactGA.initialize(initObject);
            }, 1000);
        }

        getTenantConfig({ signal }).then(handleTenantConfigResponse).catch(isCancelledErrorProcessor).catch(throwError);
    }, [signal]);

    if (customTheme === null) {
        return null;
    }

    const isTinyUrl = window.location.host.startsWith("go.");

    return (
        <ColorModeProvider>
            <ThemedApp>
                <ThemedToastContainer />
                <Suspense fallback={<SuspenseSpinner />}>
                    <ActionSpinner />
                    <CircularProgressSpinner />
                    {isTinyUrl ? (
                        <ShortUrlRouter />
                    ) : (
                        <GenericGridConfigurationManager>
                            <AppRoute />
                        </GenericGridConfigurationManager>
                    )}
                </Suspense>
            </ThemedApp>
        </ColorModeProvider>
    );
};

export default App;
